import type { Venue } from '../../types/venue';

export const mjsMalmo: Venue = {
  id: 'mjs-malmo',
  name: 'MJ\'s',
  description: {
    en: `<p>Take your next meeting to a whole new level in a fantastic, creative, and inspiring environment - in the heart of Malmö. Forget everything you've previously associated with the word conference. A conference or meeting at MJ's is always something different. Fantastic, inspiring, and fun facilities with the latest technology await you. And when we say fun, we literally mean fun. You will laugh, and you will dance. Maybe have a drink or two. Definitely bubbles and flamingos.</p>
    
    <p>With unconditional love for the city of Malmö, we have created our own world where it blooms all year round, flamingos live in the bathrooms, and where you can sit in our courtyard and enjoy dinner regardless of the weather.</p>
    
    <p>Two bars, hotel rooms, and a Mediterranean-inspired restaurant. Gym, sauna, and parking garage available. The rooms feature velvet interiors, thick soft carpets, spacious wardrobes, and wall art by photographer Nicolas Guerin. Room sizes vary between 10-40 square meters and are divided into 5 different room categories from XS-XL.</p>`,
    
    sv: `<p>Ta ditt nästa möte till en helt ny nivå i en fantastisk, kreativ och inspirerande miljö – i hjärtat av Malmö. Glöm allt du tidigare förknippat med ordet konferens. En konferens eller ett möte på MJ's är alltid något annat. Fantastiska, inspirerande och roliga faciliteter med den senaste tekniken väntar dig. Och när vi säger roligt menar vi bokstavligen roligt. Du kommer att skratta och du kommer att dansa. Kanske ta en drink eller två. Definitivt bubblor och flamingos.</p>
    
    <p>Med villkorslös kärlek för staden Malmö har vi skapat en egen värld där det blommar året om, flamingos bor i badrummen och där du kan sitta ute på vår innergård och äta middag oavsett väder.</p>
    
    <p>Två barer, hotellrum och en medelhavsinspirerad restaurang. Gym, bastu och parkeringsgarage finns. Rummen har sammetsinredning, tjocka mjuka mattor, rymliga garderober och väggkonst av fotografen Nicolas Guerin. Storleken på rummen varierar mellan 10-40 kvm och fördelas i 5 olika rumskategorier från XS-XL.</p>`,
    
    no: `<p>Ta ditt neste møte til et helt nytt nivå i et fantastisk, kreativt og inspirerende miljø - i hjertet av Malmö. Glem alt du tidligere har forbundet med ordet konferanse. En konferanse eller et møte på MJ's er alltid noe annet. Fantastiske, inspirerende og morsomme fasiliteter med den nyeste teknologien venter deg. Og når vi sier morsomt, mener vi bokstavelig talt morsomt. Du kommer til å le, og du kommer til å danse. Kanskje ta en drink eller to. Definitivt bobler og flamingoer.</p>
    
    <p>Med betingelsesløs kjærlighet for byen Malmö har vi skapt vår egen verden der det blomstrer året rundt, flamingoer bor i badeværelsene, og der du kan sitte i vår gårdsplass og nyte middag uavhengig av været.</p>
    
    <p>To barer, hotellrom og en middelhavsinspirert restaurant. Treningsstudio, badstue og parkeringshus tilgjengelig. Rommene har fløyelinteriør, tykke myke tepper, romslige garderober og veggkunst av fotograf Nicolas Guerin. Romstørrelsene varierer mellom 10-40 kvadratmeter og er delt inn i 5 forskjellige romkategorier fra XS-XL.</p>`,
    
    da: `<p>Tag dit næste møde til et helt nyt niveau i et fantastisk, kreativt og inspirerende miljø - i hjertet af Malmö. Glem alt, hvad du tidligere har forbundet med ordet konference. En konference eller et møde på MJ's er altid noget andet. Fantastiske, inspirerende og sjove faciliteter med den nyeste teknologi venter dig. Og når vi siger sjov, mener vi bogstaveligt talt sjov. Du vil grine, og du vil danse. Måske tage en drink eller to. Helt sikkert bobler og flamingoer.</p>
    
    <p>Med ubetinget kærlighed til byen Malmö har vi skabt vores egen verden, hvor det blomstrer året rundt, flamingoer bor i badeværelserne, og hvor du kan sidde i vores gård og nyde middag uanset vejret.</p>
    
    <p>To barer, hotelværelser og en middelhavsinspirekret restaurant. Fitness, sauna og parkeringsgarage tilgængelig. Værelserne har fløjlsinteriør, tykke bløde tæpper, rummelige garderober og vægkunst af fotograf Nicolas Guerin. Værelsernes størrelse varierer mellem 10-40 kvadratmeter og er opdelt i 5 forskellige værelseskategorier fra XS-XL.</p>`
  },
  image: '/assets/images/mjs.jpg',
  country: 'se',
  region: 'skane',
  city: 'Malmö',
  address: 'Master Johansgatan 13, 211 21 Malmö, Sweden',
  coordinates: {
    lat: 55.60583255942052,
    lng: 12.997933336205254
  },
  capacity: {
    min: 2,
    max: 50
  },
  rating: 4.8,
  reviews: Array.from({ length: 120 }, (_, i) => ({
    id: `review-${i}`,
    userId: `user-${i}`,
    rating: 4.8,
    comment: '',
    date: new Date().toISOString()
  })),
  amenities: ['wifi', 'coffee', 'restaurant', 'bar', 'gym', 'sauna'],
  images: [
    '/assets/images/mjs.jpg',
    'https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&q=80&w=2000'
  ],
  priceRange: {
    min: 1500,
    max: 25000
  },
  recommended: false,
  factbox: {
    rooms: 83,
    conferenceRooms: 3,
    meetingCapacity: 50,
    features: [
      '83 rooms (9 single, 74 double)',
      '3 conference rooms and 3 group rooms',
      'Room sizes 10-40 square meters (XS-XL categories)',
      'Restaurant with 85 seats',
      'Two bars with full alcohol rights',
      'Mediterranean-inspired cuisine',
      'Party capacity: 120 seated, 150 mingling',
      'Velvet interiors and artwork by Nicolas Guerin',
      'Year-round courtyard dining area',
      'Central location near Malmö Central Station',
      'Gym and sauna facilities',
      'Underground parking garage',
      'Latest technology for meetings',
      'Video conferencing capabilities',
      'Hybrid meeting setup',
      'Free wireless internet',
      'Projector and large screen',
      'KRAV-certified',
      'Swan eco-labeled',
      'Wheelchair accessible',
      'Karaoke and music entertainment',
      'Beverage tastings',
      'Team building activities'
    ]
  }
}; 